import { Activity, Dispatch, JSX, ReactNode, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import Container from '@ui/container/container';
import { SessionTypesEnum } from '@/types/session.types';
import { getUserTimezone } from '@ui/calendar/utils';
import useHasPermission from '@/utils/authentication/use-has-permission';
import { GENERAL_EDITABLE_PERMISSIONS } from '@/types/permissions.types';
import EditStateWrapper from '@ui/edit-state-wrapper/edit-state-wrapper';
import UnfocusedSessionInfoContent from './unfocused-session-info-content';
import FocusedSessionInfoContent from './focused-session-info-content';

import styles from './session-stream-info.module.css';
import useGetSessionVideo from './on-demand/use-get-session-video';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { getActiveChannel, getUser } from '@/store/slices/user/selectors';
import {
  getWorkingProjectSessions,
  selectProjectSettings,
  selectWorkingProjectDefaultLanguage,
  selectWorkingProjectLanguages,
  selectWorkingSession,
} from '@/store/slices/project/selectors';
import OptionalComponent from '@ui/optional-component/optional-component';
import getSelectedBroadcastType from './helpers/get-selected-broadcast-type';
import BroadcastTypePicker from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/broadcast-type-picker/broadcast-type-picker';
import handleBroadcastTypeChange from './helpers/handle-broadcast-type-change';
import SelectedBroadcastTypePill from '@/micros/shared-create-and-edit-components/grid-areas/main/project-overview/broadcast-type-picker/selected-broadcast-type-pill';
import SessionVodContent from './on-demand/session-vod-content';
import { VideoType } from '@/types/video.types';

type ChildrenProps = {
  isFocusedWithin: boolean;
  setIsFocusedWithin: (isFocused: boolean) => void;
};

type TSessionStreamInfoProps = {
  children?: ReactNode | ((props: ChildrenProps) => JSX.Element);
  /** if syncProjectDates true, will update the project dates when the session dates are updated */
  syncProjectDates?: boolean;
};

const SessionStreamInfo = ({ children, syncProjectDates }: TSessionStreamInfoProps) => {
  const channel = useAppSelector(getActiveChannel);
  const projectDefaultLanguage = useAppSelector(selectWorkingProjectDefaultLanguage);
  const user = useAppSelector(getUser);
  const session = useAppSelector(selectWorkingSession);
  const sessions = useAppSelector(getWorkingProjectSessions);
  const projectLanguages = useAppSelector(selectWorkingProjectLanguages);
  const workingProjectSettings = useAppSelector(selectProjectSettings);

  const [forceOpen, setForceOpen] = useState(false);
  const [showAddVideoPopover, setShowAddVideoPopover] = useState(false);
  const [selectedVodType, setSelectedVodType] = useState<VideoType | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isFocusedState, setIsFocusedState] = useState(false);
  const prevFocusRef = useRef(false);
  const prevModalOpenRef = useRef(false);
  const setIsFocusedWithinRef = useRef<((isFocused: boolean) => void) | null>(null);
  const [selectedDates, setSelectedDates] = useState<{
    startDate: number | null;
    endDate: number | null;
    selectedTimezone: string;
  }>({
    startDate: null,
    endDate: null,
    selectedTimezone: getUserTimezone(),
  });

  const { hasPermission: hasPermissions } = useHasPermission({
    allowedRoles: GENERAL_EDITABLE_PERMISSIONS,
  });

  const isOnDemand = session?.session_type === SessionTypesEnum.onDemand;
  const languageKey = projectDefaultLanguage || 'en';
  const videoUuid = isOnDemand ? (session?.static_videos?.[languageKey] ?? null) : null;
  const { sessionVideoData } = useGetSessionVideo(channel, videoUuid, session?.uuid);

  const isBreakout = session?.session_type === SessionTypesEnum.breakoutRooms;

  // For on-demand sessions, force open if no video is set. For other sessions, check if dates need to be changed
  const requiresTimeChange = !session?.timestamp || Object.values(session?.requires_time_change || {}).some(Boolean);
  const shouldForceOpen = isOnDemand ? !videoUuid : requiresTimeChange;

  const bType = getSelectedBroadcastType(session);

  const dispatch = useAppDispatch();

  useEffect(() => {
    setForceOpen(shouldForceOpen || isModalOpen);
  }, [shouldForceOpen, isModalOpen]);

  useEffect(() => {
    const wasFocused = prevFocusRef.current;
    if (isOnDemand && wasFocused && !isFocusedState) {
      // if there's no video saved, force focus back to true
      if (!sessionVideoData) {
        if (setIsFocusedWithinRef.current) {
          setIsFocusedWithinRef.current(true);
        }
        return;
      }

      // otherwise reset states as normal
      setShowAddVideoPopover(false);
      setSelectedVodType(sessionVideoData?.type ?? null);
    }
    prevFocusRef.current = isFocusedState;
  }, [isFocusedState, isOnDemand, sessionVideoData]);

  useEffect(() => {
    const wasModalOpen = prevModalOpenRef.current;
    if (wasModalOpen && !isModalOpen) {
      setSelectedVodType(sessionVideoData?.type ?? null);
      if (setIsFocusedWithinRef.current) {
        setIsFocusedWithinRef.current(false);
      }
    }
    prevModalOpenRef.current = isModalOpen;
  }, [isModalOpen, sessionVideoData?.type]);

  const handleSetIsFocusedWithChildrenHandler = useCallback(
    (setIsFocusedWithin: Dispatch<SetStateAction<boolean>>) => (isFocusedWithin: boolean) => {
      setIsFocusedWithin(isFocusedWithin);
      setForceOpen(isFocusedWithin);
    },
    []
  );

  return (
    <>
      <Container minHeight="108px" data-component="SessionStreamInfo">
        <EditStateWrapper
          disabled={!hasPermissions}
          Label="BASIC INFO"
          headerClassName="position-absolute width-100-percent"
          className={`${styles['session-stream-info']} position-relative`}
          keepOpen={forceOpen}
        >
          {({ isFocusedWithin, setIsFocusedWithin }) => {
            if (isFocusedWithin !== isFocusedState) {
              Promise.resolve().then(() => setIsFocusedState(isFocusedWithin));
            }

            setIsFocusedWithinRef.current = setIsFocusedWithin;

            return (
              <Container
                width="100%"
                height="100%"
                minHeight="84px"
                data-show-edit-label={isFocusedWithin ? 'false' : 'true'}
                transitionDuration="0ms"
                className={'margin-t-24'}
              >
                <Container
                  width="100%"
                  className={`flex jc-flex-end ai-center gap-12 ${!isFocusedWithin ? 'cursor-pointer' : ''} ${styles['content-container']}`}
                  hoverBackground={isFocusedWithin ? undefined : 'var(--gray-900)'}
                  borderRadius="8px"
                  transitionDuration={isFocusedWithin ? '0ms' : undefined}
                >
                  <OptionalComponent display={bType === SessionTypesEnum.onDemand}>
                    <SessionVodContent
                      sessionVideo={sessionVideoData}
                      isEditMode={isFocusedWithin}
                      session={session}
                      projectDefaultLanguage="en"
                      selectedType={selectedVodType}
                      onSetSelectedType={setSelectedVodType}
                      onReset={() => setShowAddVideoPopover(true)}
                      showAddPopover={showAddVideoPopover}
                      setShowAddPopover={setShowAddVideoPopover}
                      onAddPopoverClose={() => setShowAddVideoPopover(false)}
                      onModalOpenChange={setIsModalOpen}
                    />
                  </OptionalComponent>
                  <Activity mode={bType !== SessionTypesEnum.onDemand ? 'visible' : 'hidden'}>
                    <UnfocusedSessionInfoContent
                      selectedDates={selectedDates}
                      session={session}
                      isFocusedWithin={isFocusedWithin}
                    />
                    <FocusedSessionInfoContent
                      session={session}
                      syncProjectDates={syncProjectDates}
                      selectedDates={selectedDates}
                      setSelectedDates={setSelectedDates}
                      isFocusedWithin={isFocusedWithin}
                    />
                  </Activity>
                  {isFocusedWithin ? (
                    <Container className="margin-t--24">
                      <BroadcastTypePicker
                        selected={getSelectedBroadcastType(session)}
                        onChange={(type) =>
                          handleBroadcastTypeChange({
                            type,
                            sessions,
                            session,
                            user,
                            projectLanguages,
                            projectDefaultLanguage,
                            dispatch,
                            workingProjectSettings,
                          })
                        }
                        disabled={isBreakout}
                        allowFireside
                        isEditingInfo={isFocusedWithin}
                      />
                    </Container>
                  ) : (
                    <Container className="padding-r-12">
                      <SelectedBroadcastTypePill broacastType={getSelectedBroadcastType(session)} />
                    </Container>
                  )}
                </Container>
                <Container transitionDuration="0">
                  {typeof children === 'function'
                    ? children({
                        isFocusedWithin,
                        setIsFocusedWithin: handleSetIsFocusedWithChildrenHandler(setIsFocusedWithin),
                      })
                    : children}
                </Container>
              </Container>
            );
          }}
        </EditStateWrapper>
      </Container>
    </>
  );
};

export default SessionStreamInfo;
